/**
 * PeopleMemoryHealth — MS-18
 * ----------------------------------------------------------------------------
 * Painel diário do extractor de Person Memory. Mostra:
 *   • KPIs do último check: status, inserts 24h, guard rejections, acceptance %
 *   • Razões de alerta (warn/critical) — `details.reasons`
 *   • Histórico das últimas 30 execuções (line chart) com inserts vs rejections
 *
 * Lê apenas `v19_health_checks` WHERE table_name='people_memory_acceptance'.
 * Cron já agendado: `people-memory-health-check-daily` (0 6 * * *).
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, AlertCircle, ChevronDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";

type HealthStatus = "ok" | "warn" | "critical";

interface HealthRow {
  id: string;
  checked_at: string;
  status: HealthStatus;
  row_count: number;
  details: {
    inserted?: number;
    guard_rejected?: number;
    acceptance_rate?: number;
    eligible_messages?: number;
    total_audit_events?: number;
    reasons?: string[];
    window_hours?: number;
  };
}

const STATUS_BADGE: Record<HealthStatus, { variant: "default" | "destructive" | "secondary"; label: string; Icon: typeof CheckCircle2 }> = {
  ok: { variant: "default", label: "OK", Icon: CheckCircle2 },
  warn: { variant: "secondary", label: "Atenção", Icon: AlertCircle },
  critical: { variant: "destructive", label: "Crítico", Icon: AlertTriangle },
};

type HistoryRange = "14" | "30" | "90";
const RANGE_LIMIT: Record<HistoryRange, number> = { "14": 14, "30": 30, "90": 90 };

export default function PeopleMemoryHealth() {
  const [rows, setRows] = useState<HealthRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [range, setRange] = useState<HistoryRange>("30");

  const load = async (limit: number) => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("v19_health_checks")
      .select("id,checked_at,status,row_count,details")
      .eq("table_name", "people_memory_acceptance")
      .order("checked_at", { ascending: false })
      .limit(limit);
    if (err) setError(err.message);
    else setRows((data ?? []) as unknown as HealthRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load(RANGE_LIMIT[range]);
  }, [range]);

  const runNow = async () => {
    setRunning(true);
    try {
      await supabase.functions.invoke("people-memory-health-check");
      await load(RANGE_LIMIT[range]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  const latest = rows[0];
  const chartData = useMemo(
    () =>
      [...rows]
        .reverse()
        .map((r) => ({
          ts: new Date(r.checked_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
          inserted: r.details.inserted ?? 0,
          guard_rejected: r.details.guard_rejected ?? 0,
          acceptance: Math.round((r.details.acceptance_rate ?? 0) * 100),
        })),
    [rows],
  );

  const deltas = useMemo(() => {
    if (!rows.length) return null;
    const cur = rows[0];
    const prev = rows[1];
    const last7 = rows.slice(1, 8);
    const avg = (vals: number[]) =>
      vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
    const curIns = cur.details.inserted ?? 0;
    const curAcc = (cur.details.acceptance_rate ?? 0) * 100;
    const prevIns = prev?.details.inserted ?? null;
    const prevAcc = prev ? (prev.details.acceptance_rate ?? 0) * 100 : null;
    const avg7Ins = last7.length ? avg(last7.map((r) => r.details.inserted ?? 0)) : null;
    const avg7Acc = last7.length
      ? avg(last7.map((r) => (r.details.acceptance_rate ?? 0) * 100))
      : null;
    return {
      insVsPrev: prevIns === null ? null : curIns - prevIns,
      insVsAvg7: avg7Ins === null ? null : curIns - avg7Ins,
      accVsPrev: prevAcc === null ? null : curAcc - prevAcc,
      accVsAvg7: avg7Acc === null ? null : curAcc - avg7Acc,
    };
  }, [rows]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">People Memory — Health Check</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Cron diário (06:00 UTC) mede acceptance rate do extractor de
                memórias de pessoa. Alerta crítico se 0 inserts com mensagens
                elegíveis ou acceptance &lt; 5%.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={runNow} disabled={running}>
              <RefreshCw className={`h-4 w-4 mr-2 ${running ? "animate-spin" : ""}`} />
              Rodar agora
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          )}
          {error && <p className="text-sm text-destructive">Erro: {error}</p>}
          {!loading && !error && !latest && (
            <p className="text-sm text-muted-foreground">
              Sem checks registrados ainda. Clique em "Rodar agora" para gerar o primeiro.
            </p>
          )}
          {latest && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard
                label="Status"
                value={
                  <Badge variant={STATUS_BADGE[latest.status].variant} className="text-xs">
                    {(() => {
                      const Icon = STATUS_BADGE[latest.status].Icon;
                      return <Icon className="h-3 w-3 mr-1" />;
                    })()}
                    {STATUS_BADGE[latest.status].label}
                  </Badge>
                }
                hint={new Date(latest.checked_at).toLocaleString("pt-BR")}
              />
              <KpiCard
                label="Inserts (24h)"
                value={<span className="text-2xl font-bold tabular-nums">{latest.details.inserted ?? 0}</span>}
                hint={`${latest.details.eligible_messages ?? 0} mensagens elegíveis`}
                deltas={deltas ? [
                  { label: "vs anterior", value: deltas.insVsPrev, fmt: "int" },
                  { label: "vs média 7", value: deltas.insVsAvg7, fmt: "dec1" },
                ] : undefined}
              />
              <KpiCard
                label="Guard rejections"
                value={<span className="text-2xl font-bold tabular-nums">{latest.details.guard_rejected ?? 0}</span>}
                hint={`${latest.details.total_audit_events ?? 0} eventos audit`}
              />
              <KpiCard
                label="Acceptance rate"
                value={
                  <span className="text-2xl font-bold tabular-nums">
                    {((latest.details.acceptance_rate ?? 0) * 100).toFixed(1)}%
                  </span>
                }
                hint="inserted / (inserted + rejected)"
                deltas={deltas ? [
                  { label: "vs anterior", value: deltas.accVsPrev, fmt: "pp" },
                  { label: "vs média 7", value: deltas.accVsAvg7, fmt: "pp" },
                ] : undefined}
              />
            </div>
          )}

          {latest && latest.details.reasons && latest.details.reasons.length > 0 && (
            <ReasonsPanel rows={rows} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-sm">Histórico (últimos {rows.length || 0} checks)</CardTitle>
            <ToggleGroup
              type="single"
              size="sm"
              value={range}
              onValueChange={(v) => v && setRange(v as HistoryRange)}
            >
              <ToggleGroupItem value="14" className="text-xs h-7 px-2">14d</ToggleGroupItem>
              <ToggleGroupItem value="30" className="text-xs h-7 px-2">30d</ToggleGroupItem>
              <ToggleGroupItem value="90" className="text-xs h-7 px-2">90d</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sem histórico.</p>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                  <XAxis dataKey="ts" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" domain={[0, 100]} unit="%" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="left" type="monotone" dataKey="inserted" name="Inserts" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  <Line yAxisId="left" type="monotone" dataKey="guard_rejected" name="Rejected" stroke="hsl(var(--destructive))" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="acceptance" name="Acceptance %" stroke="hsl(var(--success))" strokeWidth={2} dot={false} strokeDasharray="4 2" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type DeltaFmt = "int" | "dec1" | "pp";
interface DeltaItem { label: string; value: number | null; fmt: DeltaFmt }

function formatDelta(d: DeltaItem): { text: string; tone: "up" | "down" | "flat" | "na" } {
  if (d.value === null || Number.isNaN(d.value)) return { text: "—", tone: "na" };
  const v = d.value;
  const eps = d.fmt === "int" ? 0.5 : 0.05;
  const tone: "up" | "down" | "flat" = v > eps ? "up" : v < -eps ? "down" : "flat";
  const sign = v > 0 ? "+" : "";
  let body: string;
  if (d.fmt === "int") body = `${sign}${Math.round(v)}`;
  else if (d.fmt === "dec1") body = `${sign}${v.toFixed(1)}`;
  else body = `${sign}${v.toFixed(1)}pp`;
  return { text: body, tone };
}

function KpiCard({
  label,
  value,
  hint,
  deltas,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  deltas?: DeltaItem[];
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1.5">{value}</div>
      {deltas && deltas.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5">
          {deltas.map((d, i) => {
            const { text, tone } = formatDelta(d);
            const cls =
              tone === "up"
                ? "text-success"
                : tone === "down"
                ? "text-destructive"
                : tone === "flat"
                ? "text-muted-foreground"
                : "text-muted-foreground";
            return (
              <span key={i} className="text-[11px] tabular-nums">
                <span className="text-muted-foreground">{d.label}: </span>
                <span className={cls}>{text}</span>
              </span>
            );
          })}
        </div>
      )}
      {hint && <p className="text-[11px] text-muted-foreground mt-1.5">{hint}</p>}
    </div>
  );
}

// ─── Reasons Panel ──────────────────────────────────────────────────────────
type SeverityFilter = "all" | "critical" | "warn";

interface ReasonAggEntry {
  key: string;
  total: number;
  critical: number;
  warn: number;
  examples: { text: string; status: HealthStatus; checked_at: string }[];
}

function normalizeReasonKey(raw: string): string {
  // Take stable prefix: text before ":", "=", "(" or numeric tail.
  const base = raw.split(/[:=(]/)[0].trim();
  return (base || raw).replace(/\s+/g, " ").slice(0, 80);
}

function ReasonsPanel({ rows }: { rows: HealthRow[] }) {
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState<SeverityFilter>("all");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const { aggregated, totals } = useMemo(() => {
    const map = new Map<string, ReasonAggEntry>();
    let critical = 0;
    let warn = 0;
    for (const row of rows) {
      const sev = row.status;
      if (sev !== "warn" && sev !== "critical") continue;
      const reasons = row.details.reasons ?? [];
      for (const reason of reasons) {
        const key = normalizeReasonKey(reason);
        let entry = map.get(key);
        if (!entry) {
          entry = { key, total: 0, critical: 0, warn: 0, examples: [] };
          map.set(key, entry);
        }
        entry.total++;
        if (sev === "critical") {
          entry.critical++;
          critical++;
        } else {
          entry.warn++;
          warn++;
        }
        if (entry.examples.length < 5) {
          entry.examples.push({ text: reason, status: sev, checked_at: row.checked_at });
        }
      }
    }
    const aggregated = Array.from(map.values()).sort((a, b) => b.total - a.total);
    return { aggregated, totals: { critical, warn, all: critical + warn } };
  }, [rows]);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? aggregated
        : aggregated.filter((e) => (filter === "critical" ? e.critical > 0 : e.warn > 0)),
    [aggregated, filter],
  );

  if (aggregated.length === 0) return null;

  return (
    <div className="mt-4 rounded-md border border-warning/40 bg-warning/5">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-warning/10 transition-colors rounded-md">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
            <span className="text-xs font-medium text-warning">Razões do alerta</span>
            <Badge variant="secondary" className="text-[10px] h-5">{totals.all} eventos</Badge>
            {totals.critical > 0 && (
              <Badge variant="destructive" className="text-[10px] h-5">{totals.critical} crit</Badge>
            )}
            {totals.warn > 0 && (
              <Badge variant="outline" className="text-[10px] h-5">{totals.warn} warn</Badge>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-3 pb-3 space-y-3">
          <ToggleGroup
            type="single"
            size="sm"
            value={filter}
            onValueChange={(v) => v && setFilter(v as SeverityFilter)}
          >
            <ToggleGroupItem value="all" className="text-xs h-7 px-2">Todas ({totals.all})</ToggleGroupItem>
            <ToggleGroupItem value="critical" className="text-xs h-7 px-2">Crítico ({totals.critical})</ToggleGroupItem>
            <ToggleGroupItem value="warn" className="text-xs h-7 px-2">Atenção ({totals.warn})</ToggleGroupItem>
          </ToggleGroup>

          <ul className="space-y-1.5">
            {filtered.length === 0 && (
              <li className="text-xs text-muted-foreground italic">Nenhuma razão para este filtro.</li>
            )}
            {filtered.map((entry) => {
              const isOpen = expandedKey === entry.key;
              return (
                <li key={entry.key} className="rounded border bg-background">
                  <button
                    type="button"
                    onClick={() => setExpandedKey(isOpen ? null : entry.key)}
                    className="w-full flex items-center justify-between gap-2 p-2 text-left hover:bg-muted/50 transition-colors"
                  >
                    <span className="text-xs font-mono text-foreground truncate min-w-0">{entry.key}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {entry.critical > 0 && (
                        <Badge variant="destructive" className="text-[10px] h-5 tabular-nums">{entry.critical}</Badge>
                      )}
                      {entry.warn > 0 && (
                        <Badge variant="outline" className="text-[10px] h-5 tabular-nums">{entry.warn}</Badge>
                      )}
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t px-2 py-1.5 space-y-1 bg-muted/30">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Exemplos</p>
                      {entry.examples.map((ex, i) => (
                        <div key={i} className="flex items-start gap-1.5 text-[11px]">
                          <Badge
                            variant={ex.status === "critical" ? "destructive" : "secondary"}
                            className="text-[9px] h-4 shrink-0"
                          >
                            {ex.status}
                          </Badge>
                          <span className="text-muted-foreground tabular-nums shrink-0">
                            {new Date(ex.checked_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                          </span>
                          <span className="font-mono text-foreground/80 break-all">{ex.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

